import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BetaForm = ({ open, onOpenChange, pendingSaveRecipe = null, onLoggedInForSave = null }) => {
  // When the visitor arrived by tapping "save" on a public recipe, use the
  // see-through cream overlay so they keep the recipe context behind them.
  const overlayClass = pendingSaveRecipe ? 'bg-[#EEE7D8]/60 backdrop-blur-sm' : undefined;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [emailError, setEmailError] = useState('');
  const [emailHasAccount, setEmailHasAccount] = useState(false);

  // Mid-save, logging in is a step of saving — keep them in this modal with
  // the recipe blurred behind them instead of bouncing to the login page.
  const [showInlineLogin, setShowInlineLogin] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Without a pending save (plain waitlist modal) keep the old behavior:
  // send them to the login dialog on the landing page.
  const goToLogin = () => {
    if (pendingSaveRecipe) {
      setLoginError('');
      setShowInlineLogin(true);
      return;
    }
    const returnTo = window.location.pathname + window.location.search;
    navigate(`/?login=true&returnTo=${encodeURIComponent(returnTo)}`);
  };

  const handleInlineLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.email.includes('@')) {
      setLoginError('Please enter a valid email');
      return;
    }
    if (!loginPassword) {
      setLoginError('Please enter your password');
      return;
    }
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await axios.post(`${API}/login`, {
        email: formData.email,
        password: loginPassword
      });
      localStorage.setItem('adminToken', response.data.token);
      if (response.data.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      // Straight on to the save-edit overlay — exactly where a logged-in
      // viewer lands when they tap save. The host page opens it over the
      // same blurred recipe.
      onOpenChange(false);
      if (onLoggedInForSave) onLoggedInForSave(pendingSaveRecipe);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail && detail.includes('verify your email')) {
        // Same treatment as the main login dialog: surface the backend's
        // verification message and offer a resend right here.
        setLoginError(detail);
        setNeedsVerification(true);
      } else if (err?.response?.status === 401) {
        setLoginError('Incorrect email or password');
      } else {
        setLoginError(detail || 'Trouble logging in. Please try again.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await axios.post(`${API}/resend-verification`, { email: formData.email });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to send verification email');
    }
  };

  const resetInlineLogin = () => {
    setShowInlineLogin(false);
    setLoginPassword('');
    setLoginError('');
    setNeedsVerification(false);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [alreadyOnList, setAlreadyOnList] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    identity: '',
    platforms: [],
    sharingMethodsCreator: [],
    sharesRecipes: '',
    sharingMethodsCook: [],
    recipeTracking: '',
    betaExcitement: '',
    feedbackWillingness: ''
  });

  const [otherTexts, setOtherTexts] = useState({
    platformOther: '',
    sharingCreatorOther: '',
    sharingCookOther: ''
  });

  // Calculate total steps dynamically based on identity
  const getTotalSteps = () => {
    if (!formData.identity) return 8; // Default before identity is selected
    
    if (formData.identity === 'Content creator') {
      return 7; // Basic, Identity, Platforms, Sharing Methods, Tracking, Excitement, Feedback
    } else {
      const baseSteps = 6; // Basic, Identity, Shares?, Tracking, Excitement, Feedback
      if (formData.sharesRecipes === 'Yes' || formData.sharesRecipes === 'Sometimes') {
        return 7; // Add sharing methods step
      }
      return baseSteps;
    }
  };

  const totalSteps = getTotalSteps();
  const progressPercentage = (step / totalSteps) * 100;

  const handleNext = async () => {
    // Validation for each step
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error('Please fill in all fields');
        return;
      }
      if (!formData.email.includes('@')) {
        toast.error('Please enter a valid email');
        return;
      }
      // Flag an already-used email right away, before they fill the rest.
      try {
        const res = await axios.post(`${API}/beta-submission/check-email`, { email: formData.email });
        if (res.data?.has_account) {
          // They already have a Wellby account — no waitlist needed, just log in.
          setEmailHasAccount(true);
          setEmailError('You already have a Wellby account with this email.');
          return;
        }
        if (res.data?.exists) {
          if (pendingSaveRecipe) {
            // They already signed up for the beta — don't make them re-fill
            // the form. Quietly attach the recipe to their signup instead.
            try {
              await axios.post(`${API}/beta-submission/attach-save`, {
                email: formData.email,
                first_name: formData.firstName,
                recipe_id: pendingSaveRecipe.id
              });
              setAlreadyOnList(true);
              setShowThankYou(true);
            } catch {
              toast.error('Something went wrong. Please try again.');
            }
            return;
          }
          setEmailError('There is already an account associated with this email');
          return;
        }
        setEmailError('');
      } catch {
        // If the check itself fails, let them continue; submit still guards.
        return;
      }
    }
    
    if (step === 2 && !formData.identity) {
      toast.error('Please select an option');
      return;
    }

    // Step 3 for content creator: platforms
    if (step === 3 && formData.identity === 'Content creator' && formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    // Step 4 for content creator: sharing methods
    if (step === 4 && formData.identity === 'Content creator' && formData.sharingMethodsCreator.length === 0) {
      toast.error('Please select at least one sharing method');
      return;
    }

    // Step 3 for home cook/chef: do you share?
    if (step === 3 && (formData.identity === 'Home cook' || formData.identity === 'Chef') && !formData.sharesRecipes) {
      toast.error('Please select an option');
      return;
    }

    // Step 4 for home cook/chef (if they share): how do you share?
    if (step === 4 && (formData.identity === 'Home cook' || formData.identity === 'Chef') && 
        (formData.sharesRecipes === 'Yes' || formData.sharesRecipes === 'Sometimes') &&
        formData.sharingMethodsCook.length === 0) {
      toast.error('Please select at least one sharing method');
      return;
    }

    // Recipe tracking step
    if (step === totalSteps - 2 && !formData.recipeTracking.trim()) {
      toast.error('Please answer this question');
      return;
    }

    // Beta excitement step
    if (step === totalSteps - 1 && !formData.betaExcitement.trim()) {
      toast.error('Please answer this question');
      return;
    }

    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleCheckbox = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const handleSubmit = async () => {
    // Final validation
    if (!formData.recipeTracking || !formData.betaExcitement || !formData.feedbackWillingness) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data with "Other" text values
      const submission = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        identity: formData.identity,
        platforms: formData.platforms.includes('Other') 
          ? [...formData.platforms.filter(p => p !== 'Other'), otherTexts.platformOther]
          : formData.platforms,
        sharing_methods_creator: formData.sharingMethodsCreator.includes('Other')
          ? [...formData.sharingMethodsCreator.filter(m => m !== 'Other'), otherTexts.sharingCreatorOther]
          : formData.sharingMethodsCreator,
        shares_recipes: formData.sharesRecipes,
        sharing_methods_cook: formData.sharingMethodsCook.includes('Other')
          ? [...formData.sharingMethodsCook.filter(m => m !== 'Other'), otherTexts.sharingCookOther]
          : formData.sharingMethodsCook,
        recipe_tracking: formData.recipeTracking,
        beta_excitement: formData.betaExcitement,
        feedback_willingness: formData.feedbackWillingness,
        pending_save_recipe_id: pendingSaveRecipe ? pendingSaveRecipe.id : null
      };

      await axios.post(`${API}/beta-submission`, submission);
      setShowThankYou(true);
    } catch (error) {
      console.error('Beta submission error:', error);
      // Surface the real reason (e.g. "already submitted") instead of a
      // generic failure that looks like a broken form.
      if (error.response?.status === 409) {
        if (pendingSaveRecipe) {
          // Raced past the step-1 check — still attach their recipe.
          try {
            await axios.post(`${API}/beta-submission/attach-save`, {
              email: formData.email,
              first_name: formData.firstName,
              recipe_id: pendingSaveRecipe.id
            });
            setAlreadyOnList(true);
            setShowThankYou(true);
            return;
          } catch {
            // fall through to the generic message below
          }
        }
        toast.error('There is already an account associated with this email');
      } else {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (showThankYou) {
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        identity: '',
        platforms: [],
        sharingMethodsCreator: [],
        sharesRecipes: '',
        sharingMethodsCook: [],
        recipeTracking: '',
        betaExcitement: '',
        feedbackWillingness: ''
      });
      setOtherTexts({
        platformOther: '',
        sharingCreatorOther: '',
        sharingCookOther: ''
      });
      setStep(1);
      setShowThankYou(false);
      setAlreadyOnList(false);
    }
    // Always clear the inline-login view so the next open starts fresh
    // (and never re-shows a typed password).
    resetInlineLogin();
    onOpenChange(false);
  };

  if (showInlineLogin && pendingSaveRecipe) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          overlayClassName={overlayClass}
          style={{
            backgroundColor: '#F3EEE4',
            border: '1px solid #D6D0C2',
            maxWidth: '500px'
          }}
        >
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: '28px',
                color: '#51120A'
              }}
            >
              Log in to save this recipe
            </DialogTitle>
            <p
              className="text-sm"
              style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            >
              {`Log in and \u201C${pendingSaveRecipe.title}\u201D goes straight to your Wellby.`}
            </p>
          </DialogHeader>
          <form onSubmit={handleInlineLogin} className="space-y-4 py-2" data-testid="inline-login-form">
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Email
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (loginError) setLoginError(''); }}
                className="focus:ring-0"
                style={{ backgroundColor: 'transparent', border: '1px solid #D6D0C2', boxShadow: 'none' }}
                data-testid="inline-login-email"
              />
            </div>
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Password
              </Label>
              <Input
                type="password"
                value={loginPassword}
                onChange={(e) => { setLoginPassword(e.target.value); if (loginError) setLoginError(''); }}
                className="focus:ring-0"
                autoFocus
                style={{ backgroundColor: 'transparent', border: '1px solid #D6D0C2', boxShadow: 'none' }}
                data-testid="inline-login-password"
              />
            </div>
            {loginError && (
              <p className="text-sm" style={{ color: '#B3261E', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="inline-login-error">
                {loginError}
                {needsVerification && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="underline"
                      data-testid="inline-login-resend"
                      style={{ color: '#B3261E', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Resend verification email
                    </button>
                  </>
                )}
              </p>
            )}
            <Button
              type="submit"
              disabled={isLoggingIn}
              className="w-full"
              style={{
                backgroundColor: '#51120A',
                borderColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
              data-testid="inline-login-submit"
            >
              {isLoggingIn ? 'Logging in…' : 'Log in & save'}
            </Button>
            <p className="text-sm text-center" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              New to Wellby?{' '}
              <button
                type="button"
                onClick={resetInlineLogin}
                className="underline"
                data-testid="inline-login-back"
                style={{ color: '#51120A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Join the beta
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  if (showThankYou) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent 
          overlayClassName={overlayClass}
          style={{ 
            backgroundColor: '#F3EEE4', 
            border: '1px solid #D6D0C2',
            maxWidth: '500px'
          }}
        >
          <div className="text-center py-8">
            <h2 
              style={{ 
                fontSize: '36px', 
                fontFamily: "'Instrument Serif', serif", 
                color: '#51120A',
                marginBottom: '16px'
              }}
            >
              Thank You!
            </h2>
            <p 
              style={{ 
                fontSize: '18px',
                fontFamily: "'Bricolage Grotesque', sans-serif", 
                color: '#51120A',
                marginBottom: '24px'
              }}
            >
              {alreadyOnList && pendingSaveRecipe
                ? `You're already on the list — no need to sign up again! \u201C${pendingSaveRecipe.title}\u201D will be waiting in your Wellby when it's ready.`
                : pendingSaveRecipe
                ? `We're excited to have you join the Wellby beta! \u201C${pendingSaveRecipe.title}\u201D will be waiting in your Wellby when it's ready.`
                : "We're excited to have you join the Wellby beta! We'll be in touch soon."}
            </p>
            <Button
              onClick={handleClose}
              style={{ 
                backgroundColor: '#51120A', 
                borderColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        overlayClassName={overlayClass}
        style={{ 
          backgroundColor: '#F3EEE4', 
          border: '1px solid #D6D0C2',
          maxWidth: '600px',
          minHeight: '550px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <DialogHeader>
          <DialogTitle 
            style={{ 
              fontFamily: "'Instrument Serif', serif", 
              fontSize: '28px',
              color: '#51120A' 
            }}
          >
            {pendingSaveRecipe ? 'Save this recipe to your Wellby' : 'Join the Wellby Beta'}
          </DialogTitle>
          {pendingSaveRecipe && (
            <p
              className="text-sm"
              style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            >
              {`Join the beta and \u201C${pendingSaveRecipe.title}\u201D will be waiting as your first recipe when your Wellby is ready.`}
            </p>
          )}
          {/* Progress bar */}
          <div className="mt-5">
            <div 
              style={{ 
                width: '100%',
                height: '6px',
                backgroundColor: '#D6D0C2',
                borderRadius: '3px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#51120A',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>
        </DialogHeader>

        <style>
          {`
            /* Custom checkbox styling */
            input[type="checkbox"] {
              appearance: none;
              width: 20px;
              height: 20px;
              border: 1px solid #D6D0C2;
              border-radius: 4px;
              background-color: transparent;
              cursor: pointer;
              position: relative;
              flex-shrink: 0;
            }
            
            input[type="checkbox"]:checked {
              background-color: #51120A;
              border-color: #51120A;
            }
            
            input[type="checkbox"]:checked::after {
              content: '✓';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: #F3EEE4;
              font-size: 14px;
              font-weight: bold;
            }

            /* Custom radio button styling */
            input[type="radio"] {
              appearance: none;
              width: 20px;
              height: 20px;
              border: 2px solid #51120A;
              border-radius: 50%;
              background-color: transparent;
              cursor: pointer;
              position: relative;
              flex-shrink: 0;
              z-index: 10;
            }
            
            input[type="radio"]:checked {
              background-color: transparent;
            }
            
            input[type="radio"]:checked::after {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #51120A;
            }

            /* Text input and textarea styling - TRANSPARENT with border */
            input[type="text"],
            input[type="email"],
            textarea {
              background-color: transparent !important;
              border: 1px solid #D6D0C2 !important;
              box-shadow: none !important;
              -webkit-box-shadow: none !important;
              -moz-box-shadow: none !important;
            }

            input[type="text"]:hover,
            input[type="email"]:hover,
            textarea:hover {
              background-color: #EEE7D8 !important;
              box-shadow: none !important;
              -webkit-box-shadow: none !important;
            }

            input[type="text"]:focus,
            input[type="email"]:focus,
            textarea:focus {
              background-color: transparent !important;
              border-color: #D6D0C2 !important;
              outline: none !important;
              box-shadow: none !important;
              -webkit-box-shadow: none !important;
              ring: 0 !important;
            }

            input[type="text"]:focus-visible,
            input[type="email"]:focus-visible,
            textarea:focus-visible {
              outline: none !important;
              box-shadow: none !important;
              -webkit-box-shadow: none !important;
            }

            /* Radio button container styling */
            .radio-container {
              background-color: transparent;
              border: 1px solid #D6D0C2;
            }

            .radio-container:hover {
              background-color: #EEE7D8;
              border: 1px solid #EEE7D8;
            }

            /* Checkbox container styling */
            .checkbox-container:hover {
              background-color: #EEE7D8;
            }
          `}
        </style>

        <div 
          className="flex-1 py-4"
        >
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  First Name *
                </Label>
                <Input
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="focus:ring-0"
                />
              </div>
              <div>
                <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Last Name *
                </Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="focus:ring-0"
                />
              </div>
              <div>
                <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Email *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (emailError) { setEmailError(''); setEmailHasAccount(false); } }}
                  className="focus:ring-0"
                />
                {emailError && (
                  <p className="mt-1 text-sm" style={{ color: '#B3261E', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="beta-email-error">
                    {emailError}
                    {emailHasAccount && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={goToLogin}
                          className="underline"
                          data-testid="beta-email-login-link"
                          style={{ color: '#B3261E', fontFamily: "'Bricolage Grotesque', sans-serif", background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          Log in instead
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
              <p className="text-sm" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Already a user?{' '}
                <button
                  type="button"
                  onClick={goToLogin}
                  className="underline"
                  data-testid="beta-login-link"
                  style={{ color: '#51120A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Log in
                </button>
              </p>
            </div>
          )}

          {/* Step 2: Identity */}
          {step === 2 && (
            <div className="space-y-4">
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                How do you identify? *
              </Label>
              <div className="space-y-3">
                {['Home cook', 'Content creator', 'Chef'].map((option) => (
                  <label 
                    key={option}
                    className="radio-container flex items-center gap-3 p-3 rounded cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="identity"
                      checked={formData.identity === option}
                      onChange={() => setFormData({ ...formData, identity: option })}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Content Creator - Platforms */}
          {step === 3 && formData.identity === 'Content creator' && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                Which platforms do you share recipes on? *
              </Label>
              <div className="space-y-2 mt-2">
                {['Instagram', 'TikTok', 'Substack', 'Personal blog', 'Other'].map((platform) => (
                  <label 
                    key={platform}
                    className="checkbox-container flex items-center gap-3 p-2 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes(platform)}
                      onChange={() => handleCheckbox('platforms', platform)}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {platform}
                    </span>
                  </label>
                ))}
                {formData.platforms.includes('Other') && (
                  <Input
                    placeholder="Please specify..."
                    value={otherTexts.platformOther}
                    onChange={(e) => setOtherTexts({ ...otherTexts, platformOther: e.target.value })}
                    style={{ marginTop: '8px' }}
                    className="focus:ring-0"
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 4: Content Creator - Sharing Methods */}
          {step === 4 && formData.identity === 'Content creator' && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                Logistically, how do you usually share recipes on these platforms? *
              </Label>
              <div className="space-y-2 mt-2">
                {['Manually share a link', 'Use ManyChat', 'Type it out each time', 'Paste from notes', 'Other'].map((method) => (
                  <label 
                    key={method}
                    className="checkbox-container flex items-center gap-3 p-2 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.sharingMethodsCreator.includes(method)}
                      onChange={() => handleCheckbox('sharingMethodsCreator', method)}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {method}
                    </span>
                  </label>
                ))}
                {formData.sharingMethodsCreator.includes('Other') && (
                  <Input
                    placeholder="Please specify..."
                    value={otherTexts.sharingCreatorOther}
                    onChange={(e) => setOtherTexts({ ...otherTexts, sharingCreatorOther: e.target.value })}
                    style={{ marginTop: '8px' }}
                    className="focus:ring-0"
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 3: Home Cook/Chef - Do you share? */}
          {step === 3 && (formData.identity === 'Home cook' || formData.identity === 'Chef') && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                Do you share your recipes with other people? *
              </Label>
              <div className="space-y-3 mt-2">
                {['Yes', 'No', 'Sometimes'].map((option) => (
                  <label 
                    key={option}
                    className="radio-container flex items-center gap-3 p-3 rounded cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="sharesRecipes"
                      checked={formData.sharesRecipes === option}
                      onChange={() => setFormData({ ...formData, sharesRecipes: option })}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Home Cook/Chef - How do you share? (only if Yes/Sometimes) */}
          {step === 4 && (formData.identity === 'Home cook' || formData.identity === 'Chef') && 
           (formData.sharesRecipes === 'Yes' || formData.sharesRecipes === 'Sometimes') && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                HOW do you usually share your recipes with others? *
              </Label>
              <div className="space-y-2 mt-2">
                {['Text it to them', 'Write it down for them', 'Send them a link to another blog', 'Other'].map((method) => (
                  <label 
                    key={method}
                    className="checkbox-container flex items-center gap-3 p-2 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.sharingMethodsCook.includes(method)}
                      onChange={() => handleCheckbox('sharingMethodsCook', method)}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {method}
                    </span>
                  </label>
                ))}
                {formData.sharingMethodsCook.includes('Other') && (
                  <Input
                    placeholder="Please specify (e.g., screenshot, voice message)..."
                    value={otherTexts.sharingCookOther}
                    onChange={(e) => setOtherTexts({ ...otherTexts, sharingCookOther: e.target.value })}
                    style={{ marginTop: '8px' }}
                    className="focus:ring-0"
                  />
                )}
              </div>
            </div>
          )}

          {/* Recipe Tracking - 3rd to last step */}
          {step === totalSteps - 2 && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                How do you currently keep track of your recipes? *
              </Label>
              <Textarea
                value={formData.recipeTracking}
                onChange={(e) => setFormData({ ...formData, recipeTracking: e.target.value })}
                rows={4}
                className="focus:ring-0"
              />
            </div>
          )}

          {/* Beta Excitement - 2nd to last step */}
          {step === totalSteps - 1 && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Why are you excited to be part of the Wellby beta? *
              </Label>
              <Textarea
                value={formData.betaExcitement}
                onChange={(e) => setFormData({ ...formData, betaExcitement: e.target.value })}
                rows={4}
                className="focus:ring-0"
              />
            </div>
          )}

          {/* Feedback Willingness - Final step */}
          {step === totalSteps && (
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px' }}>
                Are you open to giving occasional feedback? *
              </Label>
              <div className="space-y-3 mt-2">
                {['Yes — via chat or survey', 'No'].map((option) => (
                  <label 
                    key={option}
                    className="radio-container flex items-center gap-3 p-3 rounded cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="feedback"
                      checked={formData.feedbackWillingness === option}
                      onChange={() => setFormData({ ...formData, feedbackWillingness: option })}
                    />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with buttons */}
        <div className="flex justify-between pt-4 border-t" style={{ borderColor: '#D6D0C2' }}>
          {step > 1 ? (
            <Button
              onClick={handleBack}
              variant="outline"
              className="hover-blush transition-colors px-8 py-4 text-lg"
              style={{ 
                borderWidth: '1px',
                borderColor: '#51120A', 
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Back
            </Button>
          ) : <div />}
          
          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              className="px-8 py-4 text-lg"
              style={{ 
                backgroundColor: '#51120A', 
                borderColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-8 py-4 text-lg"
              style={{ 
                backgroundColor: '#51120A', 
                borderColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BetaForm;
